import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/pages/auth/api";
import { resolveModelRuntimeErrorMessage } from "@/blocks/error/ModelRuntimeErrorBlock";
import { ResumeGame } from "@/blocks/ResumeGame";
import { getStoredSessionToken } from "@/pages/auth/storage";
import {
  createPlayGame,
  getActivePlayGame,
  type PlayGameSnapshot,
} from "@/pages/play/api";
import {
  challengeCardsForEngineVersion,
  type ChallengeCard as ChallengeCardType,
} from "@/pages/play/catalog";
import { ChallengeCard } from "@/pages/play/ChallengeCard";
import { ChallengeSetupModal } from "@/pages/play/ChallengeSetupModal";
import { EngineVersionSwitch } from "@/pages/play/EngineVersionSwitch";
import {
  getStoredPlayEngineVersion,
  storePlayEngineVersion,
  type PlayEngineVersion,
} from "@/pages/play/engineVersion";
import type { OpeningSeedSelection } from "@/pages/play/openingSeed/openingSeedTypes";
import { useI18n } from "@/shared/i18n/LanguageContext";

function defaultVariantId(card: ChallengeCardType) {
  if (card.variants?.length) {
    return card.variants[0].id;
  }
  return card.id;
}

export function PlayPage() {
  const { copy } = useI18n();
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState<ChallengeCardType | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [side, setSide] = useState<"white" | "black">("white");
  const [hardness, setHardness] = useState(6);
  const [engineVersion, setEngineVersion] = useState<PlayEngineVersion>(() =>
    getStoredPlayEngineVersion(),
  );
  const [minutes, setMinutes] = useState(3);
  const [incrementSeconds, setIncrementSeconds] = useState(2);
  const [openingSeed, setOpeningSeed] = useState<OpeningSeedSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [activeGame, setActiveGame] = useState<PlayGameSnapshot | null>(null);

  const visibleChallengeCards = useMemo(
    () => challengeCardsForEngineVersion(engineVersion),
    [engineVersion],
  );
  const legendCards = useMemo(
    () => visibleChallengeCards.filter((card) => card.kind === "solo"),
    [visibleChallengeCards],
  );
  const crowdCards = useMemo(
    () => visibleChallengeCards.filter((card) => card.kind === "group"),
    [visibleChallengeCards],
  );
  const engineVersionLabel = copy.play.engineSwitch.versions[engineVersion].label;
  const challengeSectionLabel =
    engineVersion === "catie_3_0" ? copy.play.ctm : copy.play.legends;

  const selectEngineVersion = (nextVersion: PlayEngineVersion) => {
    setEngineVersion(nextVersion);
    storePlayEngineVersion(nextVersion);
  };

  const selectedVariant = useMemo(() => {
    if (!activeCard) {
      return null;
    }
    if (activeCard.variants?.length) {
      return (
        activeCard.variants.find((variant) => variant.id === selectedVariantId) ??
        activeCard.variants[0]
      );
    }
    if (!activeCard.modelId) {
      return null;
    }
    return {
      id: activeCard.id,
      label: "Standard",
      modelId: activeCard.modelId,
      elo: activeCard.elo,
    };
  }, [activeCard, selectedVariantId]);

  useEffect(() => {
    const sessionToken = getStoredSessionToken();
    if (!sessionToken) {
      setActiveGame(null);
      return;
    }

    let cancelled = false;
    const loadActiveGame = async () => {
      try {
        const next = await getActivePlayGame(sessionToken);
        if (cancelled) {
          return;
        }
        setActiveGame(next);
      } catch {
        if (cancelled) {
          return;
        }
        setActiveGame(null);
      }
    };

    void loadActiveGame();
    return () => {
      cancelled = true;
    };
  }, []);

  const openCard = (card: ChallengeCardType) => {
    if (activeGame) {
      navigate(`/play/session/${activeGame.game_id}`);
      return;
    }
    setActiveCard(card);
    setSelectedVariantId(defaultVariantId(card));
    setSide("white");
    setHardness(6);
    setMinutes(3);
    setIncrementSeconds(2);
    setOpeningSeed(null);
    setError(null);
  };

  const closeModal = () => {
    setActiveCard(null);
    setSelectedVariantId(null);
    setOpeningSeed(null);
    setError(null);
    setIsStarting(false);
  };

  const startChallenge = async () => {
    if (!activeCard || !selectedVariant) {
      return;
    }

    const sessionToken = getStoredSessionToken();
    if (!sessionToken) {
      navigate("/login");
      return;
    }

    setError(null);
    setIsStarting(true);

    try {
      if (activeGame) {
        navigate(`/play/session/${activeGame.game_id}`);
        return;
      }
      const created = await createPlayGame(sessionToken, {
        modelId: selectedVariant.modelId,
        elo: selectedVariant.elo,
        userColor: side,
        hardness,
        engineVersion,
        minutes,
        incrementSeconds,
        openingSeed,
      });
      navigate(`/play/session/${created.game_id}`);
    } catch (startError) {
      if (startError instanceof ApiError && startError.status === 401) {
        navigate("/login");
        return;
      }
      if (
        startError instanceof ApiError &&
        startError.status === 409 &&
        startError.message.startsWith("active game exists:")
      ) {
        try {
          const current = await getActivePlayGame(sessionToken);
          if (current) {
            setActiveGame(current);
            navigate(`/play/session/${current.game_id}`);
            return;
          }
        } catch {
          // Fall through to visible error state when the active game lookup fails.
        }
      }
      const fallback =
        startError instanceof Error ? startError.message : "failed to create challenge";
      setError(resolveModelRuntimeErrorMessage(startError, fallback));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="page-stack">
      <section className="play-floor">
        <header className="play-floor__intro">
          {copy.play.floorEyebrow ? (
            <p className="play-floor__eyebrow">{copy.play.floorEyebrow}</p>
          ) : null}
          <h1>{copy.play.floorTitle}</h1>
        </header>

        {activeGame ? (
          <ResumeGame
            gameId={activeGame.game_id}
            modelId={activeGame.model_id}
            state={activeGame.state}
            moveCount={activeGame.moves.length}
          />
        ) : null}

        <EngineVersionSwitch
          value={engineVersion}
          onChange={selectEngineVersion}
        />

        <section className="challenge-section">
          <div className="challenge-section__head">
            <span>{challengeSectionLabel}</span>
          </div>
          <div className="challenge-grid challenge-grid--legends" aria-label="Legend models">
            {legendCards.map((card) => (
              <ChallengeCard
                key={card.id}
                card={card}
                versionLabel={engineVersionLabel}
                onOpen={openCard}
              />
            ))}
          </div>
        </section>

        {crowdCards.length > 0 ? (
          <section className="challenge-section">
            <div className="challenge-section__head">
              <span>{copy.play.crowds}</span>
            </div>
            <div className="challenge-grid challenge-grid--crowds" aria-label="Crowd models">
              {crowdCards.map((card) => (
                <ChallengeCard
                  key={card.id}
                  card={card}
                  versionLabel={engineVersionLabel}
                  onOpen={openCard}
                />
              ))}
            </div>
          </section>
        ) : null}
      </section>

      {activeCard ? (
        <ChallengeSetupModal
          card={activeCard}
          selectedVariantId={selectedVariantId}
          onSelectVariant={setSelectedVariantId}
          side={side}
          onSelectSide={setSide}
          hardness={hardness}
          engineVersion={engineVersion}
          onHardnessChange={setHardness}
          minutes={minutes}
          incrementSeconds={incrementSeconds}
          onMinutesChange={setMinutes}
          onIncrementChange={setIncrementSeconds}
          onClose={closeModal}
          onStart={startChallenge}
          isStarting={isStarting}
          error={error}
          openingSeed={openingSeed}
          onOpeningSeedChange={setOpeningSeed}
        />
      ) : null}
    </div>
  );
}
